import { useState } from "react"

import {
  DEFAULT_HOURS,
  type DayHours,
  HoursEditor,
  type HoursStatus,
} from "@/components/business-profile/hours-editor"
import InputError from "@/components/input-error"
import {
  PlacesPicker,
  type PlacesPickerSelection,
} from "@/components/places-picker"
import ImageUpload from "@/components/ui/image-upload"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"

interface PartnerOption {
  uid: string
  name: string
}

interface BusinessCategoryOption {
  value: string
  label: string
}

// IMP-388: hours types/defaults now live with the shared HoursEditor. Re-export
// so the admin new/edit/show pages keep their import path.
export { DEFAULT_HOURS, type DayHours }

export interface BusinessProfileForm {
  business_name: string
  phone: string
  address: string
  website: string
  description: string
  services: string
  business_category: string
  hours: DayHours[]
  hours_status: HoursStatus
}

interface AccountFormProps {
  data: {
    account: {
      name: string
      logo: File | null
      state: "active" | "inactive" | "onboarding" | "prospect" | "provisional"
      partner_uid?: string
      demo_mode?: boolean
      landing_page_cta_enabled?: boolean
      landing_page_heading_override?: string
      landing_page_subheading_override?: string
      website_selection?: PlacesPickerSelection | null
      business_profile: BusinessProfileForm
    }
  }
  setData: (key: string, value: any) => void
  errors: Record<string, any>
  logoUrl: string | null
  partners?: PartnerOption[]
  businessCategories?: BusinessCategoryOption[]
  // IMP-252: the heading-override field only renders once a landing page has
  // been generated for this account.
  landingPageGenerated?: boolean
  // The places-search endpoint for the website lookup. This form is shared
  // across the admin and partner portals, which each have their own scoped
  // endpoint — the default (/onboarding/search) is a client-session controller
  // and 500s in those contexts, so callers must pass their own.
  searchPath?: string
  // IMP-390: the matching scoped place-details endpoint (resolves a picked
  // prediction's website + address).
  detailsPath?: string
}

export default function AccountForm({
  data,
  setData,
  errors,
  logoUrl,
  partners,
  businessCategories,
  landingPageGenerated = false,
  searchPath,
  detailsPath,
}: AccountFormProps) {
  const [preview, setPreview] = useState<string | null>(null)
  const bp = data.account.business_profile

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files[0]) {
      setData("account", { ...data.account, logo: e.target.files[0] })
      setPreview(URL.createObjectURL(e.target.files[0]))
    }
  }

  function setBp(patch: Partial<BusinessProfileForm>) {
    setData("account", {
      ...data.account,
      business_profile: { ...bp, ...patch },
    })
  }

  function updateHour(idx: number, patch: Partial<DayHours>) {
    setBp({
      hours: bp.hours.map((h, i) => (i === idx ? { ...h, ...patch } : h)),
    })
  }

  return (
    <>
      <div className="grid gap-2">
        <Label htmlFor="name">Company name</Label>

        <Input
          id="name"
          name="name"
          type="text"
          className="mt-1 block w-full"
          defaultValue={data.account.name}
          autoComplete="name"
          placeholder="Name"
          onChange={(e) =>
            setData("account", { ...data.account, name: e.target.value })
          }
        />

        <InputError message={(errors as any)?.name ?? undefined} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="name">Status</Label>

        <Select
          value={data.account.state}
          onValueChange={(val) =>
            setData("account", { ...data.account, state: val })
          }
        >
          <SelectTrigger id="state" className="w-full">
            <SelectValue placeholder="Select a status" />
          </SelectTrigger>
          <SelectContent>
            {/* IMP-509: assessment demo accounts land here unclaimed —
                admin-only; the partner portal never sees the state. */}
            <SelectItem value="provisional">Provisional</SelectItem>
            <SelectItem value="prospect">Prospect</SelectItem>
            <SelectItem value="onboarding">Onboarding</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <InputError message={(errors as any)?.state ?? undefined} />
      </div>

      {partners && partners.length > 0 && (
        <div className="grid gap-2">
          <Label htmlFor="partner">Partner</Label>
          <Select
            value={data.account.partner_uid ?? ""}
            onValueChange={(val) =>
              setData("account", { ...data.account, partner_uid: val })
            }
          >
            <SelectTrigger id="partner" className="w-full">
              <SelectValue placeholder="Select a partner" />
            </SelectTrigger>
            <SelectContent>
              {partners.map((p) => (
                <SelectItem key={p.uid} value={p.uid}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <InputError message={(errors as any)?.partner ?? undefined} />
        </div>
      )}

      <div className="grid gap-2">
        <Label>Website lookup</Label>
        <PlacesPicker
          searchPath={searchPath}
          detailsPath={detailsPath}
          value={data.account.website_selection ?? null}
          onChange={(selection) =>
            setData("account", {
              ...data.account,
              website_selection: selection,
              // Prefill the business-profile fields from the picked place so
              // the canonical inputs below reflect the selection (IMP-246).
              business_profile: selection
                ? {
                    ...bp,
                    business_name: selection.name ?? bp.business_name,
                    website: selection.website ?? bp.website,
                    address: selection.address ?? bp.address,
                  }
                : bp,
            })
          }
          ariaLabel="Search for the business or paste their website"
        />
        <p className="text-muted-foreground text-xs">
          Search for the business or paste their website, then{" "}
          <strong>pick a result</strong> (or the &ldquo;Choose&hellip;&rdquo;
          option for a raw URL). It seeds the business profile below and the
          landing-page scrape.
        </p>
        <InputError message={(errors as any)?.website_selection ?? undefined} />
      </div>

      <ImageUpload
        label="Logo"
        id="logo"
        preview={preview ?? undefined}
        currentUrl={logoUrl ?? undefined}
        onFileChange={handleFileChange}
        error={(errors as any)?.logo ?? undefined}
        buttonLabel="Upload logo"
      />

      {/* IMP-246: full business-profile editor. */}
      <div className="space-y-6 rounded-lg border p-4">
        <h3 className="text-sm font-semibold">Business profile</h3>

        <div className="grid gap-2">
          <Label htmlFor="bp_business_name">Business name</Label>
          <Input
            id="bp_business_name"
            value={bp.business_name}
            onChange={(e) => setBp({ business_name: e.target.value })}
            placeholder="Acme Corp"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="bp_phone">Phone</Label>
          <Input
            id="bp_phone"
            type="tel"
            value={bp.phone}
            onChange={(e) => setBp({ phone: e.target.value })}
            placeholder="(555) 000-0000"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="bp_address">Address</Label>
          <Input
            id="bp_address"
            value={bp.address}
            onChange={(e) => setBp({ address: e.target.value })}
            placeholder="123 Main St, New York, NY 10001"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="bp_website">Website</Label>
          <Input
            id="bp_website"
            type="url"
            value={bp.website}
            onChange={(e) => setBp({ website: e.target.value })}
            placeholder="https://yourwebsite.com"
          />
        </div>

        {businessCategories && businessCategories.length > 0 && (
          <div className="grid gap-2">
            <Label htmlFor="bp_business_category">Business category</Label>
            <Select
              value={bp.business_category || ""}
              onValueChange={(val) => setBp({ business_category: val })}
            >
              <SelectTrigger id="bp_business_category" className="w-full">
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {businessCategories.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-xs">
              Personalizes the landing-page hero with phrases tailored to this
              kind of business.
            </p>
          </div>
        )}

        <div className="grid gap-2">
          <Label>Hours</Label>
          <HoursEditor
            status={bp.hours_status}
            hours={bp.hours}
            onStatusChange={(v) => setBp({ hours_status: v })}
            onHourChange={updateHour}
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="bp_description">Description</Label>
          <Textarea
            id="bp_description"
            rows={4}
            value={bp.description}
            onChange={(e) => setBp({ description: e.target.value })}
            placeholder="Tell us what this business does…"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="bp_services">Services</Label>
          <Textarea
            id="bp_services"
            rows={5}
            value={bp.services}
            onChange={(e) => setBp({ services: e.target.value })}
            placeholder="List the services offered…"
          />
        </div>
      </div>

      <div className="grid gap-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="demo_mode" className="cursor-pointer">
            Demo mode
          </Label>
          <Switch
            id="demo_mode"
            checked={data.account.demo_mode ?? false}
            onCheckedChange={(checked) =>
              setData("account", { ...data.account, demo_mode: checked })
            }
          />
        </div>
        <p className="text-muted-foreground text-xs">
          Surfaces the curated demo catalog and onboarding suggestions for this
          account.
        </p>
        <InputError message={(errors as any)?.demo_mode ?? undefined} />
      </div>

      <div className="grid gap-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="landing_page_cta_enabled" className="cursor-pointer">
            Landing page CTA
          </Label>
          <Switch
            id="landing_page_cta_enabled"
            checked={data.account.landing_page_cta_enabled ?? true}
            onCheckedChange={(checked) =>
              setData("account", {
                ...data.account,
                landing_page_cta_enabled: checked,
              })
            }
          />
        </div>
        <p className="text-muted-foreground text-xs">
          Shows the &ldquo;Activate your receptionist&rdquo; button on the
          public landing page. When off, the hero button is hidden and the
          closing CTA becomes a &ldquo;Get in touch&rdquo; link.
        </p>
        <InputError
          message={(errors as any)?.landing_page_cta_enabled ?? undefined}
        />
      </div>

      {landingPageGenerated && (
        <div className="grid gap-2">
          <Label htmlFor="landing_page_heading_override">
            Landing page heading override
          </Label>
          <Input
            id="landing_page_heading_override"
            value={data.account.landing_page_heading_override ?? ""}
            onChange={(e) =>
              setData("account", {
                ...data.account,
                landing_page_heading_override: e.target.value,
              })
            }
          />
          <p className="text-muted-foreground text-xs">
            Replaces the generated hero heading on the public landing page.
            Leave blank to use the default.
          </p>
          <InputError
            message={
              (errors as any)?.landing_page_heading_override ?? undefined
            }
          />
        </div>
      )}

      {landingPageGenerated && (
        <div className="grid gap-2">
          <Label htmlFor="landing_page_subheading_override">
            Landing page subheading override
          </Label>
          <Textarea
            id="landing_page_subheading_override"
            rows={3}
            value={data.account.landing_page_subheading_override ?? ""}
            onChange={(e) =>
              setData("account", {
                ...data.account,
                landing_page_subheading_override: e.target.value,
              })
            }
          />
          <p className="text-muted-foreground text-xs">
            Replaces the generated hero subheading on the public landing page.
            Leave blank to use the default.
          </p>
          <InputError
            message={
              (errors as any)?.landing_page_subheading_override ?? undefined
            }
          />
        </div>
      )}
    </>
  )
}
